import React, { useEffect, useState } from "react";
import UserSidebar from "../user/components/UserSidebar";
import { ProgressBar } from "react-loader-spinner";
import Footer from "units/dashboard/components/Footer";
import Header from "units/dashboard/components/Header";
import { SidebarProvider, useSidebar } from "units/dashboard/context/SidebarProvider";
import { useLocation, useNavigate } from "react-router-dom";
import {
  CheckCircle2, XCircle, ArrowLeft, Home, Receipt, Hash,
  Wallet, ShoppingBag, Sparkles, ShieldCheck, Copy, Check,
} from "lucide-react";
import { useTranslation } from "react-i18next";

/* ================================================================
 *  Copy Row — ردیف اطلاعات با قابلیت کپی
 * ================================================================ */
const InfoRow = ({ icon: Icon, label, value, copyable = false }) => {
  const { t, i18n } = useTranslation(["user"]);

  const [copied, setCopied] = useState(false);
  const isEnglish = (i18n.language === 'en');

  const handleCopy = async () => {
    if (!copyable || !value || value === "---") return;
    try {
      await navigator.clipboard.writeText(String(value));
      setCopied(true);
      setTimeout(() => setCopied(false), 1500);
    } catch (err) {
      console.log(err);
    }
  };

  return (
    <div className="flex items-center justify-between gap-3 px-4 py-3">
      <span className="flex items-center gap-2 text-[11px] sm:text-xs font-bold text-slate-500 dark:text-slate-400">
        <Icon size={13} className="text-teal-600/80 dark:text-teal-300/80 shrink-0" />
        {label}
      </span>

      <div className="flex items-center gap-2 min-w-0">
        <span className={`text-xs sm:text-sm font-extrabold text-slate-800 dark:text-amber-100 tabular-nums truncate max-w-[180px] ${isEnglish && 'font-sans'}`}>
          {value || "---"}
        </span>
        {copyable && value && value !== "---" && (
          <button
            type="button"
            onClick={handleCopy}
            aria-label={t("paymentFeedback.copy")}
            className="shrink-0 h-6 w-6 flex items-center justify-center rounded-md text-slate-400 hover:text-teal-600 hover:bg-teal-500/10 transition-colors"
          >
            {copied ? (
              <Check size={12} className="text-emerald-500" />
            ) : (
              <Copy size={12} />
            )}
          </button>
        )}
      </div>
    </div>
  );
};

/* ================================================================
 *  Main
 * ================================================================ */
const PaymentFeedbackInner = () => {
  const { t, i18n } = useTranslation(["user"]);

  const { isSidebarOpen, toggleSidebar } = useSidebar();
  const [loading, setLoading] = useState(false);
  const navigate = useNavigate();
  const location = useLocation();

  const queryParams = new URLSearchParams(location.search);
  const statusParam = queryParams.get("status");
  const status = Number(statusParam);
  const mode = queryParams.get("mode");
  const trackingCode = queryParams.get("trackingCode");
  const transactionId = queryParams.get("transactionId");
  const isEnglish = (i18n.language === 'en');
  /* ---------- بستن سایدبار موبایل هنگام resize به دسکتاپ ---------- */
  useEffect(() => {
    const handleResize = () => {
      if (window.innerWidth >= 1024 && isSidebarOpen) toggleSidebar();
    };
    window.addEventListener("resize", handleResize);
    return () => window.removeEventListener("resize", handleResize);
  }, [isSidebarOpen, toggleSidebar]);

  /* ---------- اعتبارسنجی status ---------- */
  useEffect(() => {
    const isValidStatus = statusParam === "0" || statusParam === "1";
    if (!isValidStatus) navigate("/user/dashboard");
  }, [statusParam, navigate]);

  const isSuccess = status === 1;

  const modeLabel =
    mode === "wallet"
      ? t("paymentFeedback.mode.wallet")
      : mode === "purchase"
        ? t("paymentFeedback.mode.purchase")
        : t("paymentFeedback.mode.unknown");

  const modeIcon =
    mode === "wallet" ? Wallet : mode === "purchase" ? ShoppingBag : Receipt;

  const showTracking =
    trackingCode &&
    trackingCode !== "none" &&
    trackingCode !== "None" &&
    trackingCode !== "------";

  if (loading) {
    return (
      <div className="h-screen flex flex-col overflow-hidden bg-gradient-to-br from-slate-50 via-white to-teal-50/40 dark:from-slate-950 dark:via-slate-900 dark:to-slate-950">
        <nav className="shrink-0 z-30 h-[70px] flex items-center border-b border-teal-500/20 dark:border-teal-400/15 bg-white/85 dark:bg-slate-900/85 backdrop-blur-xl">
          <Header />
        </nav>
        <div className="flex-1 flex items-center justify-center">
          <ProgressBar visible height="80" width="80" barColor="#14B8A6" borderColor="#FCD34D" />
        </div>
      </div>
    );
  }

  return (
    <div className="h-screen flex flex-col overflow-hidden bg-gradient-to-br from-slate-50 via-white to-teal-50/40 dark:from-slate-950 dark:via-slate-900 dark:to-slate-950">

      {/* ================= Header ================= */}
      <nav className="shrink-0 z-30 h-[70px] flex items-center border-b border-teal-500/20 dark:border-teal-400/15 bg-white/85 dark:bg-slate-900/85 backdrop-blur-xl shadow-[0_4px_30px_-12px_rgba(15,118,110,0.35)] print:hidden">
        <Header />
      </nav>

      {/* ================= Body ================= */}
      <div className="flex-1 min-h-0 relative">

        {isSidebarOpen && (
          <div
            onClick={toggleSidebar}
            className="lg:hidden fixed top-[70px] inset-x-0 bottom-0 z-30 bg-slate-900/50 dark:bg-black/60 backdrop-blur-sm transition-opacity duration-300"
            aria-hidden="true"
          />
        )}

        <aside
          className={`
            fixed top-[70px] bottom-0 rtl:right-0 ltr:left-0 w-64 z-40
            transform transition-transform duration-300 ease-out
            ${isSidebarOpen ? "translate-x-0" : "rtl:translate-x-full ltr:-translate-x-full"}
            lg:!translate-x-0
            rtl:border-l ltr:border-r border-teal-500/10 dark:border-teal-400/10
            bg-gradient-to-b from-white/95 via-teal-50/60 to-white/95
            dark:from-slate-900/95 dark:via-slate-900/98 dark:to-slate-900/95
            backdrop-blur-xl shadow-[0_0_40px_-10px_rgba(15,118,110,0.4)] lg:shadow-none
            print:hidden overflow-hidden
          `}
        >
          <UserSidebar />
        </aside>

        <main className="h-full flex flex-col lg:rtl:pr-64 lg:ltr:pl-64 min-w-0">
          <div className="flex-1 min-h-0 overflow-y-auto overflow-x-hidden custom-scrollbar">
            <div className="min-h-full flex flex-col">
              <div className="flex-1 px-3 sm:px-4 md:px-6 py-6 sm:py-10 flex items-center justify-center">

                <div className="w-full max-w-md mx-auto">

                  {/* ============ Status Icon + Title ============ */}
                  <div className="text-center mb-6">
                    <span
                      className={`
                        relative inline-flex h-20 w-20 sm:h-24 sm:w-24 items-center justify-center
                        rounded-full
                        ${isSuccess
                          ? "bg-gradient-to-br from-emerald-400/30 to-teal-500/20 border-2 border-emerald-400/50"
                          : "bg-gradient-to-br from-rose-400/30 to-rose-500/20 border-2 border-rose-400/50"
                        }
                        shadow-[0_10px_40px_-12px_rgba(16,185,129,0.5)]
                        animate-[popIn_0.5s_ease-out]
                      `}
                    >
                      {isSuccess ? (
                        <CheckCircle2 size={42} className="text-emerald-500 drop-shadow-[0_2px_6px_rgba(16,185,129,0.5)]" />
                      ) : (
                        <XCircle size={42} className="text-rose-500 drop-shadow-[0_2px_6px_rgba(244,63,94,0.5)]" />
                      )}

                      {/* پالس درخشان */}
                      <span
                        className={`
                          pointer-events-none absolute inset-0 rounded-full
                          ${isSuccess ? "animate-ping bg-emerald-400/20" : "animate-ping bg-rose-400/20"}
                        `}
                        style={{ animationDuration: "2s" }}
                      />
                    </span>

                    <h1
                      className={`
                        mt-4 text-2xl sm:text-3xl font-extrabold
                        ${isSuccess
                          ? "bg-gradient-to-l from-emerald-600 to-teal-500 dark:from-emerald-300 dark:to-teal-300 bg-clip-text text-transparent"
                          : "bg-gradient-to-l from-rose-600 to-red-500 dark:from-rose-300 dark:to-red-300 bg-clip-text text-transparent"
                        }
                      `}
                    >
                      {isSuccess
                        ? t("paymentFeedback.status.successTitle")
                        : t("paymentFeedback.status.failedTitle")}
                    </h1>

                    <p className="text-xs sm:text-sm text-slate-500 dark:text-slate-400 mt-2">
                      {isSuccess
                        ? t("paymentFeedback.status.successSubtitle")
                        : t("paymentFeedback.status.failedSubtitle")}
                    </p>
                  </div>

                  {/* ============ Details strip (inline, no nested cards) ============ */}
                  <div className="rounded-2xl overflow-hidden bg-white/60 dark:bg-white/5 border border-teal-500/15 dark:border-teal-400/15 backdrop-blur-md">
                    {/* Top accent */}
                    <div
                      className={`h-[2px] ${isSuccess
                        ? "bg-gradient-to-l from-transparent via-emerald-400/80 to-transparent"
                        : "bg-gradient-to-l from-transparent via-rose-400/80 to-transparent"
                        }`}
                    />

                    {/* Header */}
                    <div className="flex items-center gap-2 px-4 py-3 border-b border-teal-500/10 dark:border-teal-400/10">
                      <span className="shrink-0 flex h-7 w-7 items-center justify-center rounded-lg bg-gradient-to-br from-amber-300/40 to-teal-400/20 border border-amber-300/40">
                        <Receipt size={13} className="text-amber-500" />
                      </span>
                      <p className="text-xs sm:text-sm font-bold text-slate-700 dark:text-amber-100">
                        {t("paymentFeedback.detailsTitle")}
                      </p>
                    </div>

                    {/* Rows — با divide-y */}
                    <div className="divide-y divide-teal-500/10 dark:divide-teal-400/10">
                      {showTracking && (
                        <InfoRow
                          icon={Hash}
                          label={t("paymentFeedback.trackingCode")}
                          value={trackingCode}
                          copyable
                        />
                      )}

                      {transactionId && (
                        <InfoRow
                          icon={Hash}
                          label={t("paymentFeedback.transactionId")}
                          value={transactionId}
                          copyable
                        />
                      )}

                      <InfoRow
                        icon={modeIcon}
                        label={t("paymentFeedback.paymentDescription")}
                        value={modeLabel}
                      />

                      {/* Status row with color */}
                      <div className="flex items-center justify-between gap-3 px-4 py-3 bg-teal-500/5 dark:bg-teal-400/5">
                        <span className="flex items-center gap-2 text-[11px] sm:text-xs font-bold text-slate-600 dark:text-amber-100">
                          {isSuccess ? (
                            <CheckCircle2 size={13} className="text-emerald-500" />
                          ) : (
                            <XCircle size={13} className="text-rose-500" />
                          )}
                          {t("paymentFeedback.status.label")}
                        </span>
                        <span
                          className={`
                            inline-flex items-center gap-1 px-2.5 py-1 rounded-lg
                            text-[11px] font-extrabold
                            ${isSuccess
                              ? "text-emerald-700 dark:text-emerald-300 bg-emerald-500/15 border border-emerald-500/30"
                              : "text-rose-700 dark:text-rose-300 bg-rose-500/15 border border-rose-500/30"
                            }
                          `}
                        >
                          {isSuccess
                            ? t("paymentFeedback.status.success")
                            : t("paymentFeedback.status.failed")}
                        </span>
                      </div>
                    </div>
                  </div>



                  {/* Trust line */}
                  <div className="mt-4 flex items-center justify-center gap-1.5 text-[10px] text-slate-400 dark:text-slate-500">
                    <ShieldCheck size={11} className="text-emerald-500/70" />
                    {t("paymentFeedback.trustLine")}
                  </div>
                </div>
              </div>

              <div className="px-3 sm:px-4 md:px-6 pb-4">
                <div className="max-w-4xl mx-auto">
                  <Footer />
                </div>
              </div>
            </div>
          </div>
        </main>
      </div>

      <style>{`
        @keyframes popIn {
          0% { transform: scale(0.5); opacity: 0; }
          60% { transform: scale(1.1); }
          100% { transform: scale(1); opacity: 1; }
        }

        .custom-scrollbar {
          scrollbar-width: thin;
          scrollbar-color: rgba(20, 184, 166, 0.55) transparent;
          scroll-behavior: smooth;
        }
        .custom-scrollbar::-webkit-scrollbar { width: 10px; height: 10px; }
        .custom-scrollbar::-webkit-scrollbar-track { background: transparent; border-radius: 999px; margin: 6px 0; }
        .custom-scrollbar::-webkit-scrollbar-thumb {
          border-radius: 999px; border: 2px solid transparent; background-clip: padding-box;
          background-image: linear-gradient(180deg, rgba(20,184,166,0.55), rgba(45,212,191,0.7), rgba(251,191,36,0.55));
        }
        .custom-scrollbar::-webkit-scrollbar-thumb:hover {
          background-image: linear-gradient(180deg, rgba(20,184,166,0.9), rgba(45,212,191,1), rgba(251,191,36,0.9));
          box-shadow: 0 0 12px -2px rgba(20,184,166,0.7);
        }
        .dark .custom-scrollbar { scrollbar-color: rgba(251,191,36,0.45) transparent; }
        .dark .custom-scrollbar::-webkit-scrollbar-thumb {
          background-image: linear-gradient(180deg, rgba(20,184,166,0.55), rgba(94,234,212,0.65), rgba(251,191,36,0.65));
        }
      `}</style>
    </div>
  );
};

const PaymentFeedback = () => (
  <SidebarProvider>
    <PaymentFeedbackInner />
  </SidebarProvider>
);

export default PaymentFeedback;